import React from 'react';
import { Thermometer, Flame, Wind, Wrench, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const ServiceCard = ({ icon: Icon, title, description, features, colorClass, bgClass, index, btnText, btnHref }) => (
  <div
    data-testid={`service-card-${index}`}
    className="service-card bg-white p-8 md:p-10 rounded-3xl border border-slate-100 shadow-[0_4px_20px_rgb(0,0,0,0.03)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] hover:border-[#0EA5E9]/30 transition-all duration-300 group"
  >
    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110 ${bgClass}`}>
      <Icon className={`w-7 h-7 ${colorClass}`} />
    </div>

    <h3
      className="text-2xl font-bold text-[#0F172A] mb-3"
      style={{ fontFamily: 'Outfit, sans-serif' }}
    >
      {title}
    </h3>

    <p className="text-slate-600 leading-relaxed mb-6">
      {description}
    </p>

    <ul className="space-y-2 mb-6">
      {features.map((feature, idx) => (
        <li key={idx} className="flex items-center gap-2 text-sm text-slate-500">
          <div className="w-1.5 h-1.5 rounded-full bg-[#0EA5E9]" />
          {feature}
        </li>
      ))}
    </ul>

    <a
      href={btnHref}
      data-testid={`service-cta-${index}`}
      className="inline-flex items-center gap-2 text-[#F97316] font-semibold hover:gap-3 transition-all duration-300"
    >
      {btnText}
      <ArrowRight className="w-4 h-4" />
    </a>
  </div>
);

const ServicesSection = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-20 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-sm font-medium tracking-wide uppercase text-[#0EA5E9] mb-4">
            Our Services
          </p>
          <h2
            data-testid="services-headline"
            className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Complete HVAC Solutions
            <br />
            for Your Home
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            From emergency repairs to new installations, our certified technicians
            deliver quality service you can trust.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          <ServiceCard
            icon={Thermometer}
            title="AC Repair & Installation"
            description="Fast, reliable air conditioning repair and professional installation services. We work with all major brands to keep you cool."
            features={['Same-day service', 'All brands serviced', 'Energy-efficient options']}
            colorClass="text-[#0EA5E9]"
            bgClass="bg-[#E0F2FE]"
            index={0}
            btnText="Schedule AC Repair"
            btnHref="/schedule-ac-repair"
          />
          <ServiceCard
            icon={Flame}
            title="Heating Services"
            description="Expert furnace repair, heat pump installation, and heating system maintenance to keep your home warm and cozy."
            features={['Furnace repair', 'Heat pump install', 'Boiler service']}
            colorClass="text-[#F97316]"
            bgClass="bg-orange-50"
            index={1}
            btnText="Schedule Heating Service"
            btnHref="/schedule-heating-service"
          />
          <ServiceCard
            icon={Wind}
            title="Duct Cleaning"
            description="Professional air duct cleaning to improve indoor air quality and HVAC efficiency. Breathe cleaner, healthier air."
            features={['Allergen removal', 'Efficiency boost', 'Full sanitization']}
            colorClass="text-[#0EA5E9]"
            bgClass="bg-[#E0F2FE]"
            index={2}
            btnText="Request Duct Cleaning"
            btnHref="/request-duct-cleaning"
          />
          <ServiceCard
            icon={Wrench}
            title="Maintenance Plans"
            description="Preventive maintenance programs to extend equipment life, prevent breakdowns, and save on energy costs."
            features={['Priority scheduling', 'Discount on repairs', 'Annual tune-ups']}
            colorClass="text-[#F97316]"
            bgClass="bg-orange-50"
            index={3}
            btnText="View Maintenance Plans"
            btnHref="/maintenance-plans"
          />
        </div>

        <div className="text-center mt-16">
          <p className="text-slate-500 mb-4">Need a full system replacement?</p>
          <a href="/instant-hvac-estimate-new-orleans">
            <Button
              data-testid="services-cta-btn"
              className="bg-[#0F172A] hover:bg-[#1e293b] text-white font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:-translate-y-1"
            >
              Get an Instant Replacement Estimate
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
